import { useState, useRef, useEffect } from 'react';
import { APP, getFileBadgeColor } from './tokens';
import { AppFolderIcon } from './AppFolderIcon';

// ─── Types ────────────────────────────────────────

export interface BrowseFile {
  name: string;
  type: 'folder' | 'file';
  fileType?: string;   // e.g. 'pdf', 'docx', 'xlsx'
  meta?: string;       // e.g. '3 pages · Dec 12'
}

export interface IntegrationFileBrowserModalProps {
  open: boolean;
  onClose: () => void;
  integrationName: string;        // e.g. 'Google Drive'
  integrationIcon: React.ReactNode;
  integrationColor: string;       // e.g. '#4285F4'
  breadcrumb: string[];           // e.g. ['My Drive', 'Client Documents']
  files: BrowseFile[];
  onBreadcrumbClick?: (index: number) => void;
  onFolderOpen?: (name: string) => void;
  onImport?: (selectedFiles: string[]) => void;
}

// ─── Component ────────────────────────────────────

export default function IntegrationFileBrowserModal({
  open,
  onClose,
  integrationName,
  integrationIcon,
  integrationColor,
  breadcrumb,
  files,
  onBreadcrumbClick,
  onFolderOpen,
  onImport,
}: IntegrationFileBrowserModalProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const backdropRef = useRef<HTMLDivElement>(null);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const toggleFile = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const selectableFiles = files.filter((f) => f.type === 'file');
  const selectedCount = selected.size;

  return (
    <div
      ref={backdropRef}
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        zIndex: 100,
      }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: 640,
        maxHeight: '80vh',
        borderRadius: 16,
        overflow: 'clip',
        backgroundColor: '#FDFCFB',
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBlock: 20,
          paddingInline: 24,
          borderBottom: '1px solid #E8DED4',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 6,
              backgroundColor: integrationColor,
              flexShrink: 0,
            }}>
              {integrationIcon}
            </div>
            <span style={{
              fontSize: 17,
              fontWeight: 600,
              color: APP.textCardBody,
              fontFamily: APP.fontFamily,
              lineHeight: '22px',
            }}>
              Import from {integrationName}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M11 3L3 11M3 3L11 11" stroke="#6B5E52" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Search bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          paddingBlock: 12,
          paddingInline: 24,
          borderBottom: '1px solid #E8DED4',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            borderRadius: 8,
            paddingBlock: 10,
            paddingInline: 14,
            gap: 8,
            backgroundColor: APP.bgCodeBlock,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="7" cy="7" r="5" stroke="#A89E94" strokeWidth="1.5" />
              <path d="M10.5 10.5L14 14" stroke="#A89E94" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span style={{
              fontSize: 14,
              color: APP.textCardMeta,
              fontFamily: APP.fontFamily,
              lineHeight: '18px',
            }}>
              Search your {integrationName}...
            </span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          paddingBlock: 12,
          paddingInline: 24,
          gap: 6,
          borderBottom: '1px solid #E8DED4',
        }}>
          {breadcrumb.map((crumb, i) => {
            const isLast = i === breadcrumb.length - 1;
            return (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {i > 0 && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M4.5 3L7.5 6L4.5 9" stroke="#A89E94" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                <button
                  type="button"
                  onClick={() => !isLast && onBreadcrumbClick?.(i)}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: isLast ? APP.textCardBody : '#D97645',
                    fontFamily: APP.fontFamily,
                    lineHeight: '16px',
                    background: 'none',
                    border: 'none',
                    cursor: isLast ? 'default' : 'pointer',
                    padding: 0,
                  }}
                >
                  {crumb}
                </button>
              </span>
            );
          })}
        </div>

        {/* File list */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlock: 4,
          overflow: 'auto',
          flexGrow: 1,
        }}>
          {files.map((file) => {
            if (file.type === 'folder') {
              return (
                <button
                  key={file.name}
                  type="button"
                  onClick={() => onFolderOpen?.(file.name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingBlock: 12,
                    paddingInline: 24,
                    gap: 12,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <AppFolderIcon width={20} height={16} />
                  <span style={{ flexGrow: 1, fontSize: 14, fontWeight: 500, color: APP.textCardBody, fontFamily: APP.fontFamily, lineHeight: '18px' }}>
                    {file.name}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M6 4L10 8L6 12" stroke="#A89E94" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              );
            }

            const isSelected = selected.has(file.name);
            const badgeBg = getFileBadgeColor(file.fileType ?? 'pdf');
            const badgeLabel = (file.fileType ?? 'FILE').slice(0, 3).toUpperCase();

            return (
              <button
                key={file.name}
                type="button"
                onClick={() => toggleFile(file.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBlock: 12,
                  paddingInline: 24,
                  gap: 12,
                  background: isSelected ? '#D976450F' : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {/* File type badge — matches existing FileTypeBadge pattern */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 14,
                  height: 16,
                  flexShrink: 0,
                  borderRadius: 2,
                  backgroundColor: badgeBg,
                }}>
                  <span style={{ fontSize: 6, fontWeight: 700, color: '#FFFFFF', fontFamily: APP.fontFamily, lineHeight: '8px' }}>
                    {badgeLabel}
                  </span>
                </div>

                {/* File info */}
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: APP.textCardBody, fontFamily: APP.fontFamily, lineHeight: '18px' }}>
                    {file.name}
                  </span>
                  {file.meta && (
                    <span style={{ fontSize: 12, color: APP.textCardMeta, fontFamily: APP.fontFamily, lineHeight: '16px' }}>
                      {file.meta}
                    </span>
                  )}
                </div>

                {/* Checkbox */}
                {isSelected ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    borderRadius: 4,
                    backgroundColor: '#D97645',
                  }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2.5 5.5L4 7L7.5 3.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                ) : (
                  <div style={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    borderRadius: 4,
                    border: `1.5px solid ${APP.dotSeparator}`,
                  }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBlock: 16,
          paddingInline: 24,
          borderTop: '1px solid #E8DED4',
        }}>
          <span style={{ fontSize: 13, color: APP.textCardDetail, fontFamily: APP.fontFamily, lineHeight: '16px' }}>
            {selectedCount > 0 ? `${selectedCount} file${selectedCount !== 1 ? 's' : ''} selected` : `${selectableFiles.length} files available`}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                paddingBlock: 9,
                paddingInline: 18,
                border: '1px solid #E8DED4',
                background: 'none',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                color: APP.textCardBody,
                fontFamily: APP.fontFamily,
                lineHeight: '18px',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { if (selectedCount > 0) onImport?.(Array.from(selected)); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                paddingBlock: 9,
                paddingInline: 18,
                border: 'none',
                backgroundColor: selectedCount > 0 ? '#D97645' : '#D9764580',
                cursor: selectedCount > 0 ? 'pointer' : 'default',
                fontSize: 14,
                fontWeight: 500,
                color: '#FFFFFF',
                fontFamily: APP.fontFamily,
                lineHeight: '18px',
              }}
            >
              {selectedCount > 0 ? `Import ${selectedCount} file${selectedCount !== 1 ? 's' : ''}` : 'Import files'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
